var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import { distributionChannels, ENVIRONMENT_DOMAINS } from '../constants';
import { EnvironmentTypes } from '../types';
import { getServerEnvironment, isCSR } from './domain';
import { formatLocaleForIntl } from './i18n';
import { getEnvironmentByAwsVariables } from './i18nSharedConfig';
import { getDomainConfig } from '../routes/domainConfig';
export const sleep = (time = 1000) => __awaiter(void 0, void 0, void 0, function* () { return new Promise((res) => setTimeout(res, time)); });
/**
 * Gets URL Parameter by provided key.
 *
 * By default takes current page url.
 *
 * @returns string | null
 */
export const getUrlParamByKey = (key, url = isCSR() ? window.location.href : null) => {
    if (!url)
        return null;
    const replKey = key.replace(/[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${replKey}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results)
        return null;
    if (!results[2])
        return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
};
export const removeUrlParamByKey = (param, url = window.location.href) => {
    const urlObj = new URL(url);
    urlObj.searchParams.delete(param);
    return urlObj.toString();
};
// Removes all faulsy values, empty arrays and empty objects from the given object
export const removeEmptyPropsFromObj = (obj) => Object.entries(obj)
    .map(([key, value]) => [
    key,
    value && typeof value === 'object' ? removeEmptyPropsFromObj(value) : value,
])
    .reduce((acc, [key, value]) => {
    if (value === null ||
        value === '' ||
        (typeof value === 'object' && Object.keys(value).length === 0)) {
        return acc;
    }
    acc[key] = value;
    return acc;
}, {});
export const mergeDeep = (target, source) => {
    const merged = Object.assign({}, target);
    for (const key in source) {
        if (Object.hasOwn(source, key)) {
            if (source[key] && typeof source[key] === 'object') {
                if (Array.isArray(source[key]) && Array.isArray(merged[key])) {
                    merged[key] = source[key].length > 0 ? [...source[key]] : [...merged[key]];
                }
                else {
                    if (typeof merged[key] !== 'object' || target[key] === null) {
                        merged[key] = Array.isArray(source[key]) ? [] : {};
                    }
                    merged[key] = mergeDeep(merged[key], source[key]);
                }
            }
            else {
                merged[key] = source[key];
            }
        }
    }
    return merged;
};
export const getDomainWithEnvPrefix = (siteUrl) => {
    const currentEnv = getEnvironmentByAwsVariables(process.env.AWS_BRANCH);
    if (currentEnv === EnvironmentTypes.LOCAL)
        return 'localhost:3000';
    const domainPrefix = ENVIRONMENT_DOMAINS[currentEnv];
    const URLParts = siteUrl.split('/');
    const domain = URLParts[2].split('.');
    domain[1] = `${domainPrefix}${domain[1]}`;
    return domain.join('.');
};
export const getUSPostLoginBaseUrl = () => {
    switch (getServerEnvironment()) {
        case 'development':
            return 'https://app.dev-raisin.com/en-us';
        case 'staging':
            return 'https://app.staging-raisin.com/en-us';
        case 'onboarding':
            return 'https://app.onboarding-raisin.com/en-us';
        case 'production':
            return 'https://app.raisin.com/en-us';
        default:
            return 'http://localhost:3000/en-us';
    }
};
export const getLocalePrefix = (siteUrl) => {
    if (getEnvironmentByAwsVariables(process.env.AWS_BRANCH) === EnvironmentTypes.LOCAL)
        return '';
    const URLParts = siteUrl.split('/');
    return URLParts.length > 3 ? `/${URLParts[3]}` : '';
};
export const getDistributionChannel = (locale) => distributionChannels[formatLocaleForIntl(locale)] || distributionChannels['de-DE'];
export const isAppDuringBuildProcess = () => process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD;
/**
 * Returns the provided value if it's a valid (non-falsy) value.
 * Otherwise, it falls back to a default value of '--,--'.
 *
 * @param {string|number} value - The value to check and return.
 * @returns {string|number} - The original value if it's truthy, otherwise the fallback '--,--'.
 */
export const FALLBACK_VALUE = '--,--';
export const getValueWithFallback = (value) => value || FALLBACK_VALUE;
/**
 * Returns the truncated text.
 *
 * @param {string} text - The text to truncate and return.
 * @param {number} end - The last index to truncate.
 * @param {number} start - The first index to start truncating, default is 0.
 * @param {boolean} addElipses - Boolean value to concat ellipses(...).
 * @returns {string} - The truncated text.
 */
export const truncateText = (text, end, start = 0, addElipses = true) => {
    if (!text || start < 0 || end > text.length || start >= end) {
        return text || '';
    }
    let truncatedText = text.slice(start, end);
    if (addElipses && text.length > end) {
        truncatedText += '...';
    }
    return truncatedText;
};
/**
 * Returns a translated term period based on the given period and unit.
 *
 * @param {number} period - The numerical value representing the period.
 * @param {string} unit - The unit of the period (e.g., "days", "weeks", "months", "years").
 * @param {{ days: string; weeks: string; months: string; years: string }} KEYS - An object containing translation keys for each time unit.
 * @returns {[string, { period: number }] | undefined} - An array containing the translated key and period, or undefined if input is invalid.
 */
export const getTranslatedTermPeriod = (period, unit, KEYS) => {
    if (!period || !unit) {
        return undefined;
    }
    const isYearConvertable = unit === 'months' && period > 11 && period % 6 === 0;
    if (isYearConvertable) {
        const yearPeriod = period / 12;
        return [KEYS.years, { period: yearPeriod }];
    }
    return [KEYS[unit], { period }];
};
/**
 * Determines if the current page is being rendered in Safari browser.
 *
 *
 * @returns true or false
 */
export const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
export const getLocalizedUrl = (path, locale) => {
    if (typeof path !== 'string') {
        return '';
    }
    const { basePath } = getDomainConfig(locale);
    const normalizedPath = `/${path.replace(/^\/+/, '')}`;
    const cleanedPath = normalizedPath.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/|$)/i, '');
    return (basePath + cleanedPath).replace(/\/{2,}/g, '/') || '/';
};
//# sourceMappingURL=helpers.js.map